import React from 'react';
import { useMapLink } from './MapLinkContext';

interface Column {
  key: string;
  label: string;
}

interface Row {
  [key: string]: unknown;
  map_link_id?: string;
  mapLinkId?: string;
}

function getRowLinkId(row: Row): string | undefined {
  return (row.map_link_id ?? row.mapLinkId) as string | undefined;
}

interface TableData {
  columns?: Column[];
  rows?: Row[];
}

interface ProjectAppTableBlockProps {
  data: Record<string, unknown>;
}

const ProjectAppTableBlock: React.FC<ProjectAppTableBlockProps> = ({
  data,
}) => {
  const { hoveredLinkId, setHoveredLinkId } = useMapLink();
  const { columns, rows: rawRows } = data as TableData;

  // Normalise row keys to match column keys (handles camelCase ↔ snake_case mismatches from Kysely)
  const rows = React.useMemo(() => {
    if (!rawRows || !columns) return rawRows;
    const colKeys = columns.map((c) => c.key);
    const toSnake = (s: string) => s.replace(/([A-Z])/g, '_$1').toLowerCase();
    return rawRows.map((row) => {
      const out: Row = {};
      for (const [k, v] of Object.entries(row)) {
        const snake = toSnake(k);
        if (colKeys.includes(k)) {
          out[k] = v;
        } else if (colKeys.includes(snake)) {
          out[snake] = v;
        } else {
          out[k] = v;
        }
      }
      return out;
    });
  }, [rawRows, columns]);

  console.log(
    '[TableBlock] render — hoveredLinkId:',
    hoveredLinkId,
    'row keys:',
    rows?.[0] ? Object.keys(rows[0]) : 'no rows',
    'linkIds:',
    rows?.map((r) => getRowLinkId(r)),
  );

  if (!columns || !rows || columns.length === 0) {
    return <div className='project-app-block-table--empty'>No table data</div>;
  }

  // Filter out map_link_id / mapLinkId from visible columns
  const visibleColumns = columns.filter(
    (col) => col.key !== 'map_link_id' && col.key !== 'mapLinkId',
  );

  return (
    <div className='project-app-block-table'>
      <table>
        <thead>
          <tr>
            {visibleColumns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const linkId = getRowLinkId(row);
            const isHighlighted = linkId != null && linkId === hoveredLinkId;
            return (
              <tr
                key={i}
                className={isHighlighted ? 'highlighted' : ''}
                onMouseEnter={() => {
                  console.log(
                    '[TableBlock] mouseEnter row',
                    i,
                    'linkId:',
                    linkId,
                  );
                  if (linkId) setHoveredLinkId(linkId);
                }}
                onMouseLeave={() => {
                  console.log(
                    '[TableBlock] mouseLeave row',
                    i,
                    'linkId:',
                    linkId,
                  );
                  if (linkId) setHoveredLinkId(null);
                }}
              >
                {visibleColumns.map((col) => (
                  <td key={col.key}>{String(row[col.key] ?? '')}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ProjectAppTableBlock;
